import { createContext, useContext, useEffect, useState } from "react";
import { doc, setDoc, updateDoc, Timestamp, orderBy, collection, onSnapshot, query, deleteDoc } from "firebase/firestore";
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, onAuthStateChanged, signOut, getAuth, updateProfile, sendPasswordResetEmail, confirmPasswordReset } from "firebase/auth";
import { db, storage } from "./db";
import { toast } from "react-toastify";
import CryptoJS from "crypto-js";
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
const auth = getAuth();
const userAuthContext = createContext();

export function UserAuthContextProvider({ children }) {
	const [user, setUser] = useState({});
	const [notes, setNotes] = useState([]);
	const [progress, setProgress] = useState(0);
	const [url, setUrl] = useState(null);
	const [files, setFiles] = useState([]);
	const [passwords, setPasswords] = useState("");
	const key = process.env.REACT_APP_SECRET_KEY;

	function login(email, password) {
		return signInWithEmailAndPassword(auth, email, password).catch((error) => {
			toast.error(error.message);
		});
	}
	function register(name, email, password) {
		return createUserWithEmailAndPassword(auth, email, password).then((userCredential) => {
			const user = userCredential.user;
			updateProfile(user, {
				displayName: name,
				photoURL: `https://avatars.dicebear.com/api/bottts/${name}.svg`,
			}).catch((error) => {
				toast.error(error.message);
			});
		});
	}

	function logOut() {
		return signOut(auth).catch((error) => {
			toast.error(error.message);
		});
	}

	const encryptData = (message) => {
		return CryptoJS.AES.encrypt(message, key).toString();
	};

	const decryptData = (encryptedData) => {
		return CryptoJS.AES.decrypt(encryptedData, key).toString(CryptoJS.enc.Utf8);
	};

	async function addNote(note) {
		const noteRef = doc(collection(db, `users/${user.uid}/notes`));
		await setDoc(noteRef, {
			note: encryptData(note),
			id: noteRef.id,
			created: Timestamp.now(),
		});
	}

	const deleteNote = async (id) => {
		await deleteDoc(doc(db, `users/${user.uid}/notes/${id}`));
	};

	const getNotes = async (id) => {
		const q = query(collection(db, `users/${id}/notes`), orderBy("created"));
		onSnapshot(q, (snapshot) => {
			let todos = [];
			snapshot.docs.forEach((doc) => {
				todos.push({ note: decryptData(doc.data().note), id: doc.id });
			});
			setNotes(todos);
		});
	};

	const uploadFile = async (file) => {
		const filesRef = doc(collection(db, `users/${user.uid}/files`));
		const storageRef = ref(storage, `files/${user.uid}/${file.name}`);
		const uploadTask = uploadBytesResumable(storageRef, file);
		uploadTask.on(
			"state_changed",
			(snap) => {
				let percentage = (snap.bytesTransferred / snap.totalBytes) * 100;
				setProgress(percentage);
			},
			(err) => {
				toast.error(err.message);
			},
			async () => {
				let url = "";
				await getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
					url = downloadURL;
				});
				await setDoc(filesRef, {
					url: url,
					id: filesRef.id,
					name: file.name,
					created: Timestamp.now(),
				});
				setUrl(url);
			}
		);
	};

	const deleteFile = async (id, name) => {
		const fileRef = ref(storage, `files/${user.uid}/${name}`);
		await deleteObject(fileRef).catch((error) => {
			toast.error(error);
		});
		await deleteDoc(doc(db, `users/${user.uid}/files/${id}`));
	};

	const getFiles = async (id) => {
		const q = query(collection(db, `users/${id}/files`), orderBy("created"));
		onSnapshot(q, (snapshot) => {
			let documents = [];
			snapshot.docs.forEach((doc) => {
				documents.push({ ...doc.data(), id: doc.id });
			});
			setFiles(documents);
		});
	};

	const savePassword = async (sitename, username, password, id) => {
		const passwordRef = doc(collection(db, `users/${user.uid}/passwords`));
		const passwordReference = doc(db, `users/${user.uid}/passwords/${id}`);
		if (id) {
			await updateDoc(passwordReference, {
				sitename: sitename,
				username: encryptData(username),
				password: encryptData(password),
				id: passwordRef.id,
			});
		} else {
			await setDoc(passwordRef, {
				sitename: sitename,
				username: encryptData(username),
				password: encryptData(password),
				id: passwordRef.id,
				created: Timestamp.now(),
			});
		}
	};

	const updatePassword = async (sitename, username, password, id) => {
		const passwordRef = doc(collection(db, `users/${user.uid}/passwords/${id}`));

		await updateDoc(passwordRef, {
			sitename: sitename,
			username: encryptData(username),
			password: encryptData(password),
			id: passwordRef.id,
			created: Timestamp.now(),
		});
	};

	const getPasswords = async (id) => {
		const q = query(collection(db, `users/${id}/passwords`), orderBy("created"));
		onSnapshot(q, (snapshot) => {
			let documents = [];
			snapshot.docs.forEach((doc) => {
				documents.push({ username: decryptData(doc.data().username), sitename: doc.data().sitename, password: decryptData(doc.data().password), id: doc.id });
			});
			setPasswords(documents);
		});
	};

	const deletePassword = async (id) => {
		await deleteDoc(doc(db, `users/${user.uid}/passwords/${id}`));
	};

	const forgotPassword = async (email) => {
		return sendPasswordResetEmail(auth, email, {
			url: "https://securespace.vercel.app/login",
		})
			.then(() => {
				toast.success("Email has been successfully sent to your registered email.");
			})
			.catch((error) => {
				toast.error(error.message);
			});
	};

	const resetPassword = async (oobCode, newPassword) => {
		return confirmPasswordReset(auth, oobCode, newPassword)
			.then(() => {
				toast.success("Your password has been successfully reset.");
			})
			.catch((error) => {
				toast.error(error.message);
			});
	};

	useEffect(() => {
		const unsubscribe = onAuthStateChanged(auth, (currentuser) => {
			if (currentuser !== null) {
				setUser(currentuser);
				getNotes(currentuser.uid);
				getFiles(currentuser.uid);
				getPasswords(currentuser.uid);
			}
		});

		return () => {
			unsubscribe();
		};
	}, []);

	return <userAuthContext.Provider value={{ user, login, register, logOut, addNote, getNotes, notes, deleteNote, uploadFile, progress, url, getFiles, files, deleteFile, encryptData, decryptData, savePassword, passwords, updatePassword, getPasswords, deletePassword, forgotPassword, resetPassword }}>{children}</userAuthContext.Provider>;
}

export function useUserAuth() {
	return useContext(userAuthContext);
}
